// registry.ts — artifact viewers as plugins, chosen by mime type.
//
// A viewer registers against an exact media type ("application/json"), a
// wildcard ("image/*"), or nothing at all (the fallback, which every
// artifact can use). Resolution is exact, then wildcard, then fallback, so a
// specific viewer always beats a generic one for the same type and an
// unknown type still renders as a name, a size, its metadata and a download.
//
// Viewers load lazily: `load` is a dynamic import, which the bundler splits
// into its own chunk, so the WebGL splat renderer is only fetched the first
// time someone opens a splat. `fetchContent` says whether the host should
// download the bytes before mounting the viewer; the fallback never does,
// since a 300 MB file it cannot show is not worth fetching.
import type { ComponentType } from 'react';
import type { Artifact } from '../api';
import type { CameraSync } from './cameraSync';

export interface ArtifactViewerProps {
  artifact: Artifact;
  /** The bytes; null for a viewer registered with fetchContent: false. */
  blob: Blob | null;
  /** Shared camera for viewers shown side by side (splats); optional. */
  sync?: CameraSync | null;
}

export interface ViewerPlugin {
  id: string;
  label: string;
  /** Exact media type, "type/*", or "*" for the fallback. */
  mime: string;
  fetchContent: boolean;
  /** Whether several of these side by side benefit from a shared camera. */
  comparable?: boolean;
  load: () => Promise<{ default: ComponentType<ArtifactViewerProps> }>;
}

const exact = new Map<string, ViewerPlugin>();
const wildcard = new Map<string, ViewerPlugin>();
let fallback: ViewerPlugin | null = null;

export function registerViewer(p: ViewerPlugin): void {
  const m = p.mime.toLowerCase();
  if (m === '*') fallback = p;
  else if (m.endsWith('/*')) wildcard.set(m.slice(0, -2), p);
  else exact.set(m, p);
}

// baseType strips parameters ("text/plain; charset=utf-8" → "text/plain").
export function baseType(mime: string): string {
  return mime.split(';')[0].trim().toLowerCase();
}

export function viewerFor(mime: string): ViewerPlugin {
  const m = baseType(mime);
  const hit = exact.get(m) ?? wildcard.get(m.split('/')[0]);
  if (hit) return hit;
  if (!fallback) throw new Error('artifact registry: no fallback viewer registered');
  return fallback;
}

// ─── built-ins ──────────────────────────────────────────────────────────

registerViewer({
  id: 'fallback',
  label: 'Details',
  mime: '*',
  fetchContent: false,
  load: () => import('./FallbackViewer'),
});

registerViewer({
  id: 'image',
  label: 'Image',
  mime: 'image/*',
  fetchContent: true,
  load: () => import('./ImageViewer'),
});

for (const mime of ['application/json', 'text/*']) {
  registerViewer({
    id: 'text',
    label: 'Text',
    mime,
    fetchContent: true,
    load: () => import('./TextViewer'),
  });
}

// openscan's gaussian splats (OSPL v1/v2), rendered with openscan's own
// WebGL2 renderer.
registerViewer({
  id: 'splat',
  label: 'Splats',
  mime: 'application/vnd.openscan.ospl',
  fetchContent: true,
  comparable: true,
  load: () => import('./SplatViewer'),
});
